import { useState, useEffect } from 'react'
import { User, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useUpdateProfile } from '@/hooks'

interface User {
  id: string
  email: string
  name: string
  role: string
}

interface ProfileSectionProps {
  user: User
  onUpdate?: (updatedUser: User) => void
}

export function ProfileSection({ user, onUpdate }: ProfileSectionProps) {
  const { updateProfile, loading, error } = useUpdateProfile()
  const [name, setName] = useState(user.name)
  const [isEditing, setIsEditing] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Update local state when user prop changes
  useEffect(() => {
    setName(user.name)
  }, [user.name])

  const handleSave = async () => {
    // Validation: name cannot be empty
    const trimmedName = name.trim()
    if (!trimmedName) {
      setErrorMessage('Name cannot be empty')
      setShowError(true)
      setTimeout(() => setShowError(false), 3000)
      return
    }

    // Validation: name must be at least 2 characters
    if (trimmedName.length < 2) {
      setErrorMessage('Name must be at least 2 characters long')
      setShowError(true)
      setTimeout(() => setShowError(false), 3000)
      return
    }

    // Call API to update profile
    const result = await updateProfile({ name: trimmedName })

    if (result && result.user) {
      // Success
      setShowSuccess(true)
      setShowError(false)
      setIsEditing(false)

      // Update localStorage
      const updatedUser = { ...user, name: result.user.name }
      localStorage.setItem('user', JSON.stringify(updatedUser))

      // Notify parent component
      if (onUpdate) {
        onUpdate(updatedUser)
      }

      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000)
    } else {
      // Error
      setErrorMessage(error || 'Failed to update profile. Please try again.')
      setShowError(true)
      setTimeout(() => setShowError(false), 3000)
    }
  }

  const handleCancel = () => {
    setName(user.name)
    setIsEditing(false)
    setShowError(false)
  }

  const handleEdit = () => {
    setIsEditing(true)
    setShowError(false)
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Success Toast */}
      {showSuccess && (
        <div className="mb-4 fixed bottom-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md shadow-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="text-sm text-green-800 dark:text-green-200 font-medium">
              Profile updated successfully!
            </span>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {showError && (
        <div className="mb-4 fixed bottom-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md shadow-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <span className="text-sm text-red-800 dark:text-red-200 font-medium">
              {errorMessage}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <User className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-semibold">Profile Information</h2>
      </div>

      {/* Profile Fields */}
      <div className="space-y-4">
        {/* Name Field */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Name
          </label>
          {isEditing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="Enter your name"
              disabled={loading}
              autoFocus
            />
          ) : (
            <p className="text-foreground py-2">{user.name}</p>
          )}
        </div>

        {/* Email Field (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Email Address
          </label>
          <p className="text-foreground py-2 text-muted-foreground">{user.email}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Email cannot be changed. Contact support if you need to update your email.
          </p>
        </div>

        {/* Role Field (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Role
          </label>
          <p className="text-foreground py-2 capitalize">
            {user.role.replace('_', ' ')}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex gap-3">
        {!isEditing ? (
          <Button
            onClick={handleEdit}
            variant="default"
          >
            Edit Profile
          </Button>
        ) : (
          <>
            <Button
              onClick={handleSave}
              variant="default"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
